import { useEffect, useMemo, useRef, useState } from 'react';

type LogEntry = {
  id: string;
  type: 'log' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: string;
};

const MAX_LOGS = 200;
const DEV_CONSOLE_STORAGE_KEY = 'dev_console_enabled';
const DEV_CONSOLE_CHANNEL = 'dev_console_channel';

const formatMessage = (args: unknown[]) =>
  args
    .map((value) => {
      if (value instanceof Error) {
        return value.stack || value.message;
      }
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return '[Object]';
        }
      }
      return String(value);
    })
    .join(' ');

export function EarlyDevConsole() {
  const envEnabled = import.meta.env.DEV || import.meta.env.VITE_DEV_CONSOLE_ENABLED === 'true';
  const [enabled, setEnabled] = useState(() => {
    if (typeof window === 'undefined') return envEnabled;
    const stored = window.localStorage.getItem(DEV_CONSOLE_STORAGE_KEY);
    const queryEnabled = new URLSearchParams(window.location.search).has('devconsole');
    if (stored === null) {
      return envEnabled || queryEnabled;
    }
    return stored === 'true';
  });
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const originalConsole = useRef({
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
  });

  const appendLog = (type: LogEntry['type'], args: unknown[]) => {
    setLogs((prev) => {
      const entry: LogEntry = {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        type,
        message: formatMessage(args),
        timestamp: new Date().toLocaleTimeString(),
      };
      const next = [...prev, entry];
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        const channel = new BroadcastChannel(DEV_CONSOLE_CHANNEL);
        channel.postMessage({ type: 'log', payload: entry });
        channel.close();
      }
      return next.length > MAX_LOGS ? next.slice(-MAX_LOGS) : next;
    });
  };

  useEffect(() => {
    if (!enabled) return undefined;

    const handleError = (event: ErrorEvent) => {
      appendLog('error', [event.error || event.message]);
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      appendLog('error', [event.reason || 'Unhandled promise rejection']);
    };

    console.log = (...args: unknown[]) => {
      originalConsole.current.log(...args);
      appendLog('log', args);
    };
    console.info = (...args: unknown[]) => {
      originalConsole.current.info(...args);
      appendLog('info', args);
    };
    console.warn = (...args: unknown[]) => {
      originalConsole.current.warn(...args);
      appendLog('warn', args);
    };
    console.error = (...args: unknown[]) => {
      originalConsole.current.error(...args);
      appendLog('error', args);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      console.log = originalConsole.current.log;
      console.info = originalConsole.current.info;
      console.warn = originalConsole.current.warn;
      console.error = originalConsole.current.error;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [enabled]);

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DEV_CONSOLE_STORAGE_KEY, String(next));
    }
  };

  const openDetachedConsole = () => {
    if (typeof window === 'undefined') return;
    const popup = window.open('', 'room-dev-console', 'width=420,height=680');
    if (!popup) return;

    popup.document.write(`<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Room Dev Console</title>
          <style>
            body { margin: 0; font-family: monospace; background: #111827; color: #e5e7eb; }
            header { padding: 8px 12px; background: #1f2937; position: sticky; top: 0; }
            #log { padding: 12px; display: grid; gap: 6px; }
            .entry { font-size: 12px; }
            .time { color: #9ca3af; margin-right: 6px; }
            .error { color: #f87171; }
          </style>
        </head>
        <body>
          <header>Room Dev Console (detached)</header>
          <div id="log"></div>
          <script>
            const logEl = document.getElementById('log');
            const channel = new BroadcastChannel('${DEV_CONSOLE_CHANNEL}');
            const append = (entry) => {
              const div = document.createElement('div');
              div.className = 'entry';
              const typeClass = entry.type === 'error' ? 'error' : '';
              div.innerHTML = '<span class="time">[' + entry.timestamp + ']</span>' +
                '<span class="' + typeClass + '">' + entry.type.toUpperCase() + '</span> ' +
                '<span>' + entry.message + '</span>';
              logEl.appendChild(div);
            };
            channel.onmessage = (event) => {
              if (event.data?.type === 'log') {
                append(event.data.payload);
              }
            };
          </script>
        </body>
      </html>`);
    popup.document.close();

    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel(DEV_CONSOLE_CHANNEL);
      logs.forEach((entry) => channel.postMessage({ type: 'log', payload: entry }));
      channel.close();
    }
  };

  const badge = useMemo(() => logs.filter((log) => log.type === 'error').length, [logs]);

  if (!enabled) {
    return (
      <button
        type="button"
        onClick={toggleEnabled}
        style={{
          position: 'fixed',
          bottom: '18px',
          right: '18px',
          zIndex: 9999,
          background: '#111827',
          color: '#fff',
          borderRadius: '999px',
          padding: '8px 12px',
          border: 'none',
          fontSize: '12px',
        }}
      >
        Enable Dev Console
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        style={{
          position: 'fixed',
          bottom: '18px',
          right: '18px',
          zIndex: 9999,
          background: '#111827',
          color: '#fff',
          borderRadius: '999px',
          padding: '8px 12px',
          border: 'none',
          fontSize: '12px',
        }}
      >
        Dev Console {badge ? `(${badge})` : ''}
      </button>
      {isOpen ? (
        <div
          style={{
            position: 'fixed',
            inset: '12px',
            background: 'rgba(17, 24, 39, 0.95)',
            color: '#e5e7eb',
            zIndex: 9998,
            borderRadius: '12px',
            padding: '12px',
            overflow: 'auto',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <strong>Dev Console (early)</strong>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={openDetachedConsole}
                style={{
                  background: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 10px',
                }}
              >
                Detach
              </button>
              <button
                type="button"
                onClick={() => setLogs([])}
                style={{
                  background: '#374151',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 10px',
                }}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={toggleEnabled}
                style={{
                  background: '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 10px',
                }}
              >
                Disable
              </button>
            </div>
          </div>
          <div style={{ display: 'grid', gap: '6px' }}>
            {logs.length === 0 ? (
              <div>No logs captured yet.</div>
            ) : (
              logs.map((log) => (
                <div key={log.id} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  <span style={{ color: '#9ca3af' }}>[{log.timestamp}]</span>{' '}
                  <span style={{ color: log.type === 'error' ? '#f87171' : '#e5e7eb' }}>
                    {log.type.toUpperCase()}
                  </span>{' '}
                  <span>{log.message}</span>
                </div>
              ))
            )}
          </div>
        </div>
      ) : null}
    </>
  );
}
